/**
 * Spec 027: a brand's default app environment and testing banner, read from
 * the production API by every client, the staging site included (D1). The web
 * only uses `testingBanner`: the website does not follow the default
 * environment (D4).
 */

export type BrandEnvironmentSettings = {
  brandSite: string;
  defaultEnvironment: 'PRODUCTION' | 'STAGING';
  testingBanner: boolean;
};

export const ENVIRONMENT_SETTINGS_QUERY =
  'query MyEnvironmentSettings { myEnvironmentSettings { brandSite defaultEnvironment testingBanner } }';

export const ENVIRONMENT_SETTINGS_TIMEOUT_MS = 4000;

export function environmentSettingsCacheKey(brandSite: string): string {
  return `environment_settings_v1_${brandSite}`;
}

/**
 * The production GraphQL endpoint. A production build already calls it; a
 * staging build needs `REACT_APP_API_URL_PROD` (static access, see
 * `web-environment.guard.ts`). Null when the build does not know it.
 */
export function productionGraphqlUrl(
  apiUrlProd: string | undefined = process.env.REACT_APP_API_URL_PROD,
): string | null {
  const base = apiUrlProd?.trim();
  if (!base || base.startsWith('replace_with_')) return null;
  return `${base.endsWith('/') ? base : `${base}/`}graphql`;
}

/** Only a well-formed answer counts; anything else is "no answer". */
export function parseEnvironmentSettings(value: unknown): BrandEnvironmentSettings | null {
  const v = value as Partial<BrandEnvironmentSettings> | null | undefined;
  if (!v || typeof v.testingBanner !== 'boolean' || typeof v.brandSite !== 'string') return null;
  if (v.defaultEnvironment !== 'PRODUCTION' && v.defaultEnvironment !== 'STAGING') return null;
  return {
    brandSite: v.brandSite,
    defaultEnvironment: v.defaultEnvironment,
    testingBanner: v.testingBanner,
  };
}

export function readCachedEnvironmentSettings(
  brandSite: string,
  storage: Pick<Storage, 'getItem'> | null = safeLocalStorage(),
): BrandEnvironmentSettings | null {
  try {
    const raw = storage?.getItem(environmentSettingsCacheKey(brandSite));
    return raw ? parseEnvironmentSettings(JSON.parse(raw)) : null;
  } catch {
    return null;
  }
}

/**
 * Asks production for the brand's settings and caches a good answer. Never
 * throws: a failure (network, CORS, an older API, a timeout) returns null.
 */
export async function fetchEnvironmentSettings(options: {
  brandSite: string;
  url?: string | null;
  fetchImpl?: typeof fetch;
  storage?: Pick<Storage, 'setItem'> | null;
  timeoutMs?: number;
}): Promise<BrandEnvironmentSettings | null> {
  const url = options.url === undefined ? productionGraphqlUrl() : options.url;
  const fetchImpl = options.fetchImpl ?? (typeof fetch === 'function' ? fetch : undefined);
  if (!url || !fetchImpl) return null;
  const controller = typeof AbortController === 'function' ? new AbortController() : null;
  const timer = setTimeout(
    () => controller?.abort(),
    options.timeoutMs ?? ENVIRONMENT_SETTINGS_TIMEOUT_MS,
  );
  try {
    const response = await fetchImpl(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-Site-Host': options.brandSite },
      body: JSON.stringify({ query: ENVIRONMENT_SETTINGS_QUERY }),
      signal: controller?.signal,
    });
    if (!response.ok) return null;
    const body = await response.json();
    const settings = parseEnvironmentSettings(body?.data?.myEnvironmentSettings);
    if (settings) {
      try {
        const storage = options.storage === undefined ? safeLocalStorage() : options.storage;
        storage?.setItem(environmentSettingsCacheKey(options.brandSite), JSON.stringify(settings));
      } catch {
        // private mode: keep the answer for this page only
      }
    }
    return settings;
  } catch {
    return null;
  } finally {
    clearTimeout(timer);
  }
}

/**
 * Spec 027 FR-008: the banner shows on staging unless the brand turned it off.
 * No answer yet (and nothing cached) shows it.
 */
export function testingBannerShown(
  isStaging: boolean,
  settings: BrandEnvironmentSettings | null,
): boolean {
  return isStaging && settings?.testingBanner !== false;
}

function safeLocalStorage(): Storage | null {
  try {
    return typeof window !== 'undefined' ? window.localStorage : null;
  } catch {
    return null;
  }
}
